/**
 * Pinia clientsWorkspace store -- Phase 2.3.
 *
 * Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
 * Pebble: Phase 2.3 Clients workspace (attention-first IA rethink).
 *
 * The original ``stores/clients.js`` (Phase F5) backs the legacy CRUD
 * surface (name + email + phone + status + notes +
 * store attach/detach). This store backs the NEW workspace:
 *
 *   * attention-first list view (filter chips + name search + sort)
 *   * per-Client rollup (store count, pending approvals, invitation
 *     status, attention signals)
 *   * detail-page panels (embedded approvals queue, activity stream,
 *     auto-saving notes)
 *
 * Endpoints consumed:
 *
 *   GET  /api/clients/summary
 *        Per-client rollup; backs the list view.
 *
 *   GET  /api/clients/<id>/activity
 *        Chronological event list; backs the Activity panel.
 *
 *   GET  /api/approval_records?client_id=<n>
 *        Filtered F1.e queue; backs the Approvals panel.
 *
 *   PATCH /api/clients/<id>
 *        SAFRS PATCH on the existing Client surface. Used by the Notes
 *        panel to persist the ``notes`` column (auto-save debounce
 *        owned in this store).
 *
 *   POST /api/clients/<id>/invitations
 *        F5 endpoint, used by the detail-page Resend action.
 *
 *   POST /api/clients/<id>/invitations/<inv_id>/revoke
 *        F5 endpoint, used by the detail-page Revoke action.
 *
 * Filter / sort semantics
 * -----------------------
 *
 * Per the IA rethink, the DEFAULT view is "all clients sorted by
 * attention-needed, then by recent activity" -- NOT pre-filtered by
 * state. The filter chips ("issues" / "onboarding" / "active" /
 * "inactive" / "quiet" / "all") narrow the visible set; they do NOT
 * change the sort.
 *
 *   issues     -- attention_signals.length > 0
 *   onboarding -- invitation_status in {"invited", "expired"} (the
 *                 healthy in-progress + the broken-and-needs-resend
 *                 sub-state both belong here)
 *   active     -- attention_signals empty AND last_activity_at within
 *                 the activity window (30 days)
 *   inactive   -- lifecycle status == "inactive" (the soft-delete state).
 *                 Distinct from the "active"/"quiet" ACTIVITY chips: it
 *                 keys on the ``status`` field, not last_activity_at.
 *   quiet      -- attention_signals empty AND last_activity_at outside
 *                 the activity window
 *   all        -- everything
 *
 * Search matches name, contact name, email, phone, AND store_names (the "Sarah's Tee
 * Boutique" use case where the Distributor remembers the store but not
 * the Client).
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import api from '@/services/api'
import { emitSidebarRefresh } from '@/stores/sidebarRefresh'

// Activity window for "active vs quiet" filter, in milliseconds.
// 30 days mirrors the backend's ACCOUNT_INACTIVE_DAYS attention-signal
// threshold; the two need not match but staying coherent keeps the
// surface intuitive.
const ACTIVITY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000

// Auto-save debounce for the Notes editor. Long enough that fast-
// typists don't hammer the API; short enough that the Distributor's
// "did it save?" question answers itself.
const NOTES_DEBOUNCE_MS = 800

// Debounce for inline field auto-save on the client detail page (text
// fields). Shorter than the notes editor: these are single-line values.
const FIELD_DEBOUNCE_MS = 600

/** Server-side reason a PATCH was refused, else a generic fallback. */
function saveErrorMessage(e) {
    const data = e?.response?.data
    const detail =
        (Array.isArray(data?.errors) && data.errors[0]?.detail) ||
        data?.detail ||
        data?.message
    return detail || 'Could not save. Try again.'
}

export const FILTER_CHIPS = [
    'issues',
    'onboarding',
    'active',
    'inactive',
    'quiet',
    'all',
]

export const useClientsWorkspaceStore = defineStore('clientsWorkspace', () => {
    // ---- state ----
    const clients = ref([])
    const filter = ref('all')
    const search = ref('')
    const loading = ref(false)
    const error = ref(null)

    // Per-Client detail caches (keyed by client id as string).
    const activityByClient = ref({})
    const approvalsByClient = ref({})

    // Notes auto-save state. ``pendingNotes`` holds the in-flight string;
    // ``notesTimers`` holds the per-Client debounce handle so we can
    // cancel an in-flight save when a newer edit arrives.
    const notesTimers = {}
    // Inline field auto-save (client detail page). Keyed ``<id>:<field>``;
    // state is 'pending' | 'saving' | 'saved' | 'error', the same vocabulary
    // as ``notesSaveState`` above (one pattern, not a second one).
    const fieldTimers = {}
    const fieldGen = {}
    const fieldInflight = {}
    const fieldSent = {}
    // Generation of the newest save RUN (one that has started), per field.
    const fieldRunning = {}
    // Generation allowed to report a final state (newest save, or the
    // in-flight run after a cancel).
    const fieldLatest = {}
    // Highest generation cancelled per field: queued runs at or below it never send.
    const fieldCancelled = {}
    const fieldSaveState = ref({})
    const fieldSaveError = ref({})
    const notesSaveState = ref({}) // { [id]: 'idle' | 'saving' | 'saved' | 'error' }

    // Epoch guard for impersonation -- mirror the pattern in
    // ``stores/clients.js``. Bump on ``clear()`` so a late response from
    // a previous tenant cannot land.
    let epoch = 0

    // ---- getters ----

    /**
     * Score a Client row for the attention-first sort order. Lower
     * numbers surface earlier. Each attention signal counts; ties
     * resolve to most-recent activity_at.
     */
    function attentionScore(row) {
        const signals = Array.isArray(row.attention_signals)
            ? row.attention_signals.length
            : 0
        // Negate so MORE signals == LOWER (earlier) score.
        return -signals
    }

    const attentionCount = computed(() => {
        let n = 0
        for (const c of clients.value) {
            if (
                Array.isArray(c.attention_signals) &&
                c.attention_signals.length > 0
            ) {
                n += 1
            }
        }
        return n
    })

    /**
     * Filter chip predicate. Returns true when the row matches the
     * currently-selected chip.
     */
    function matchesFilter(row, chip) {
        const signals = row.attention_signals || []
        const invStatus = row.invitation_status
        if (chip === 'all') return true
        if (chip === 'issues') return signals.length > 0
        if (chip === 'onboarding') {
            return invStatus === 'invited' || invStatus === 'expired'
        }
        // "inactive" keys on the LIFECYCLE ``status`` field (the
        // soft-delete state), NOT the activity window. It is a DIFFERENT
        // concept from the "active"/"quiet" activity chips below, which key
        // on attention_signals + last_activity_at; a deactivated client
        // with recent activity is still "inactive" here.
        if (chip === 'inactive') return row.status === 'inactive'
        if (chip === 'active' || chip === 'quiet') {
            if (signals.length > 0) return false
            const at = row.last_activity_at
                ? Date.parse(row.last_activity_at)
                : null
            if (at == null || Number.isNaN(at)) {
                // No activity timestamp -> treat as quiet.
                return chip === 'quiet'
            }
            const fresh = Date.now() - at < ACTIVITY_WINDOW_MS
            return chip === 'active' ? fresh : !fresh
        }
        return true
    }

    /**
     * Name / email / phone / store search predicate.
     *
     * Searches lowercased substrings across:
     *   - row.name
     *   - row.contact_name
     *   - row.email
     *   - row.phone
     *   - any string in row.store_names
     *
     * Empty search matches everything (callers do this check; we keep
     * the guard here so we can be used directly from a list filter).
     */
    function matchesSearch(row, queryRaw) {
        const q = (queryRaw || '').trim().toLowerCase()
        if (!q) return true
        const haystack = [
            row.name,
            row.contact_name,
            row.email,
            row.phone,
            ...(Array.isArray(row.store_names) ? row.store_names : []),
        ]
            .filter(Boolean)
            .map((s) => String(s).toLowerCase())
            .join('\n')
        return haystack.includes(q)
    }

    /**
     * Visible clients: filter chip + search query + attention-first
     * sort. The sort is stable enough for tests because we secondary-
     * sort by ``last_activity_at`` desc (recency), which is monotonic.
     */
    const visibleClients = computed(() => {
        const f = filter.value
        const q = search.value
        const rows = clients.value
            .filter((r) => matchesFilter(r, f))
            .filter((r) => matchesSearch(r, q))
            .slice() // copy before sort
        rows.sort((a, b) => {
            const da = attentionScore(a) - attentionScore(b)
            if (da !== 0) return da
            const ta = a.last_activity_at
                ? Date.parse(a.last_activity_at)
                : 0
            const tb = b.last_activity_at
                ? Date.parse(b.last_activity_at)
                : 0
            return tb - ta
        })
        return rows
    })

    function getClientById(id) {
        if (id == null) return null
        const target = String(id)
        return clients.value.find((c) => String(c.id) === target) ?? null
    }

    // ---- actions ----

    // ``distributorId`` names the distributor whose clients to load. A
    // non-impersonating Super Admin MUST name one (the server returns
    // nothing otherwise); other callers are scoped by the server already.
    // The last named distributor is remembered so refreshClient() and other
    // bare refetches keep the same scope.
    let scopedDistributorId = null
    async function fetchClients(distributorId = undefined) {
        if (distributorId !== undefined) scopedDistributorId = distributorId
        const myEpoch = epoch
        loading.value = true
        error.value = null
        try {
            const res =
                scopedDistributorId != null
                    ? await api.get('/clients/summary', {
                          params: { distributor_id: scopedDistributorId },
                      })
                    : await api.get('/clients/summary')
            if (myEpoch !== epoch) return
            clients.value = Array.isArray(res?.data?.clients)
                ? res.data.clients
                : []
        } catch (e) {
            if (myEpoch !== epoch) return
            error.value = e?.message || 'failed to load clients'
            clients.value = []
        } finally {
            if (myEpoch === epoch) loading.value = false
        }
    }

    function applyFilter(chip) {
        if (!FILTER_CHIPS.includes(chip)) return
        filter.value = chip
    }

    function applySearch(text) {
        search.value = text || ''
    }

    async function refreshClient(id) {
        // The summary endpoint doesn't expose a single-client surface;
        // refetch the whole list. Cheap and rare (called after Resend /
        // Revoke / Notes save) and keeps the rollup coherent.
        await fetchClients()
    }

    /**
     * Fetch the activity stream for a Client. Cached per Client until
     * an explicit refresh.
     */
    async function fetchActivity(clientId) {
        const key = String(clientId)
        const myEpoch = epoch
        try {
            const res = await api.get(`/clients/${clientId}/activity`)
            if (myEpoch !== epoch) return []
            const events = Array.isArray(res?.data?.events)
                ? res.data.events
                : []
            activityByClient.value = {
                ...activityByClient.value,
                [key]: events,
            }
            return events
        } catch (e) {
            if (myEpoch !== epoch) return []
            return []
        }
    }

    /**
     * Fetch the F1.e approval queue scoped to a single Client.
     */
    async function fetchApprovals(clientId) {
        const key = String(clientId)
        const myEpoch = epoch
        try {
            const res = await api.get(
                `/approval_records?client_id=${encodeURIComponent(clientId)}`,
            )
            if (myEpoch !== epoch) return []
            const rows = Array.isArray(res?.data?.approval_records)
                ? res.data.approval_records
                : []
            approvalsByClient.value = {
                ...approvalsByClient.value,
                [key]: rows,
            }
            return rows
        } catch (e) {
            if (myEpoch !== epoch) return []
            return []
        }
    }

    /**
     * Debounced PATCH to /api/clients/<id> with the notes column. The
     * Distributor's Notes editor calls this on every change; the
     * debounce flushes after NOTES_DEBOUNCE_MS of quiescence.
     *
     * Per-Client save state is exposed via ``notesSaveState`` so the UI
     * can render "Saving..." / "Saved" inline.
     */
    function updateNotes(id, text) {
        const key = String(id)
        notesSaveState.value = {
            ...notesSaveState.value,
            [key]: 'pending',
        }
        if (notesTimers[key]) {
            clearTimeout(notesTimers[key])
        }
        notesTimers[key] = setTimeout(async () => {
            const myEpoch = epoch
            notesSaveState.value = {
                ...notesSaveState.value,
                [key]: 'saving',
            }
            try {
                await api.patch(`/clients/${id}`, {
                    data: {
                        type: 'Client',
                        id: String(id),
                        attributes: { notes: text || '' },
                    },
                })
                if (myEpoch !== epoch) return
                notesSaveState.value = {
                    ...notesSaveState.value,
                    [key]: 'saved',
                }
            } catch (e) {
                if (myEpoch !== epoch) return
                notesSaveState.value = {
                    ...notesSaveState.value,
                    [key]: 'error',
                }
            }
        }, NOTES_DEBOUNCE_MS)
    }

    /**
     * Auto-save ONE Client attribute through the SAFRS PATCH surface.
     *
     * The same debounce + state vocabulary as ``updateNotes`` (pending ->
     * saving -> saved | error), generalized per field so the detail page
     * edits name / phone / status / email inline. Only the edited field is
     * sent, never the whole form. ``delayMs: 0`` saves now (selects, and the
     * email field's blur/confirm, which is a login-identity change and must
     * never fire on a half-typed value). Resolves true when the PATCH landed,
     * false when it failed or was superseded/cancelled by a newer call.
     */
    function saveClientField(
        id,
        field,
        value,
        { delayMs = FIELD_DEBOUNCE_MS } = {},
    ) {
        const key = `${id}:${field}`
        const setState = (st, msg = '') => {
            fieldSaveState.value = { ...fieldSaveState.value, [key]: st }
            fieldSaveError.value = { ...fieldSaveError.value, [key]: msg }
        }
        // Each call is a generation: only the newest one may report a final
        // state, so an older save landing late cannot say "saved" over a newer
        // edit that is still pending or in flight.
        const gen = (fieldGen[key] = (fieldGen[key] || 0) + 1)
        fieldLatest[key] = gen
        const isLatest = () => fieldLatest[key] === gen
        setState('pending')
        if (fieldTimers[key]) {
            clearTimeout(fieldTimers[key].timer)
            fieldTimers[key].resolve(false)
            delete fieldTimers[key]
        }
        return new Promise((resolve) => {
            const run = async () => {
                delete fieldTimers[key]
                const myEpoch = epoch
                // Serialize per field: wait for any earlier PATCH to settle so
                // two requests for one field never race (last arrival wins).
                const prev = fieldInflight[key] || Promise.resolve()
                const mine = prev.then(async () => {
                    if (myEpoch !== epoch) return resolve(false)
                    if (gen <= (fieldCancelled[key] || 0)) return resolve(false)
                    fieldRunning[key] = gen
                    if (isLatest()) setState('saving')
                    const prevSent = fieldSent[key]
                    fieldSent[key] = value
                    try {
                        await api.patch(`/clients/${id}`, {
                            data: {
                                type: 'Client',
                                id: String(id),
                                attributes: { [field]: value },
                            },
                        })
                        if (myEpoch !== epoch) return resolve(false)
                        const idx = clients.value.findIndex(
                            (c) => String(c.id) === String(id),
                        )
                        if (idx >= 0) {
                            clients.value.splice(idx, 1, {
                                ...clients.value[idx],
                                [field]: value,
                            })
                        }
                        if (field === 'status') emitSidebarRefresh('clients')
                        if (isLatest()) setState('saved')
                        resolve(true)
                    } catch (e) {
                        if (myEpoch !== epoch) return resolve(false)
                        // Not sent after all: forget it so retyping the same
                        // value re-sends instead of looking already-saved.
                        if (prevSent === undefined) delete fieldSent[key]
                        else fieldSent[key] = prevSent
                        if (isLatest()) setState('error', saveErrorMessage(e))
                        resolve(false)
                    }
                })
                fieldInflight[key] = mine
                mine.finally(() => {
                    if (fieldInflight[key] === mine) delete fieldInflight[key]
                })
            }
            if (delayMs <= 0) {
                run()
            } else {
                fieldTimers[key] = {
                    timer: setTimeout(run, delayMs),
                    resolve,
                }
            }
        })
    }

    /**
     * Drop a field's PENDING (debounced, not yet sent) save and reset its
     * state. For the editor that reverted the value, or blanked a required
     * one: without this the stale timer would still PATCH the abandoned value.
     * A PATCH already in flight is not recalled; compare against
     * ``lastSentValue`` to decide whether a corrective save is needed.
     */
    function cancelClientField(id, field) {
        const key = `${id}:${field}`
        // The in-flight run (if any) becomes the "latest" again, so it settles
        // the field's state (saved / error) when it lands instead of leaving
        // the abandoned edit's "pending" behind.
        fieldCancelled[key] = fieldGen[key] || 0
        fieldLatest[key] = fieldInflight[key] ? fieldRunning[key] : undefined
        if (fieldTimers[key]) {
            clearTimeout(fieldTimers[key].timer)
            fieldTimers[key].resolve(false)
            delete fieldTimers[key]
        }
        if (!fieldInflight[key]) {
            fieldSaveState.value = { ...fieldSaveState.value, [key]: 'idle' }
            fieldSaveError.value = { ...fieldSaveError.value, [key]: '' }
        }
    }

    /** The value most recently PATCHed for a field (undefined if none). */
    function lastSentValue(id, field) {
        return fieldSent[`${id}:${field}`]
    }

    /**
     * Set a Client's lifecycle ``status`` via a SAFRS PATCH.
     *
     * The distributor Clients table replaced hard-delete with a
     * reversible status toggle: "deactivate" moves a Client to
     * ``inactive`` and "reactivate" moves it back to ``active``, leaving
     * the row in place (still listed + filterable) rather than removing
     * it, so its stores and history survive. PATCHes the same
     * capability-gated (``client.update`` = SA + Owner) / tenant-scoped
     * SAFRS Client surface via the JSON:API envelope, mirroring the
     * ``stores/clients.js`` action.
     *
     * On success the local summary row is flipped to the new ``status``
     * in place (the /clients/summary endpoint carries no single-client
     * surface, and the only field that changes is ``status``), so the
     * table re-renders the row without a full refetch.
     */
    async function setClientStatus(id, status) {
        const myEpoch = epoch
        await api.patch(`/clients/${id}`, {
            data: {
                type: 'Client',
                id: String(id),
                attributes: { status },
            },
        })
        if (myEpoch !== epoch) return
        const idx = clients.value.findIndex(
            (c) => String(c.id) === String(id),
        )
        if (idx >= 0) {
            clients.value.splice(idx, 1, {
                ...clients.value[idx],
                status,
            })
        }
        // Sidebar refresh (generic delegation): announce that the Clients
        // summary counter changed so the distributor sidebar coarse-refetches.
        // Emitted ONLY here, after the PATCH resolves + the local row updates --
        // a rejected PATCH throws above and never reaches this line, so a
        // failed toggle fires no event. The single funnel (both the deactivate
        // + reactivate wrappers route through setClientStatus) means one emit
        // covers every current + future caller. One line, no bespoke event type.
        emitSidebarRefresh('clients')
    }

    // Soft-delete / restore wrappers over setClientStatus. Named for the
    // user-facing affordances so callers read cleanly.
    async function deactivateClient(id) {
        return setClientStatus(id, 'inactive')
    }
    async function reactivateClient(id) {
        return setClientStatus(id, 'active')
    }

    /**
     * Resend an invitation for a Client. F5 endpoint -- the server
     * mints a fresh token + supersedes the previous row.
     */
    async function resendInvitation(clientId, email, firstName, lastName) {
        const payload = { email }
        if (firstName) payload.first_name = firstName
        if (lastName) payload.last_name = lastName
        await api.post(`/clients/${clientId}/invitations`, payload)
        await refreshClient(clientId)
    }

    /**
     * Revoke a specific invitation.
     */
    async function revokeInvitation(clientId, invitationId) {
        await api.post(
            `/clients/${clientId}/invitations/${invitationId}/revoke`,
        )
        await refreshClient(clientId)
    }

    function clear() {
        epoch += 1
        scopedDistributorId = null
        clients.value = []
        activityByClient.value = {}
        approvalsByClient.value = {}
        notesSaveState.value = {}
        fieldSaveState.value = {}
        fieldSaveError.value = {}
        for (const o of [fieldGen, fieldSent, fieldRunning, fieldCancelled, fieldLatest]) {
            for (const k of Object.keys(o)) delete o[k]
        }
        for (const k of Object.keys(fieldTimers)) {
            clearTimeout(fieldTimers[k].timer)
            fieldTimers[k].resolve(false)
            delete fieldTimers[k]
        }
        for (const k of Object.keys(notesTimers)) {
            clearTimeout(notesTimers[k])
            delete notesTimers[k]
        }
        filter.value = 'all'
        search.value = ''
        error.value = null
        loading.value = false
    }

    return {
        // state
        clients,
        filter,
        search,
        loading,
        error,
        activityByClient,
        approvalsByClient,
        notesSaveState,
        fieldSaveState,
        fieldSaveError,
        // getters
        visibleClients,
        attentionCount,
        getClientById,
        matchesFilter,
        matchesSearch,
        // actions
        fetchClients,
        applyFilter,
        applySearch,
        refreshClient,
        fetchActivity,
        fetchApprovals,
        updateNotes,
        saveClientField,
        cancelClientField,
        lastSentValue,
        setClientStatus,
        deactivateClient,
        reactivateClient,
        resendInvitation,
        revokeInvitation,
        clear,
        // exposed for tests
        _ACTIVITY_WINDOW_MS: ACTIVITY_WINDOW_MS,
        _NOTES_DEBOUNCE_MS: NOTES_DEBOUNCE_MS,
    }
})
